import { c as _c } from "react/compiler-runtime";
import React from 'react';
export type SubmissionPhase = 'idle' | 'uploading' | 'signing' | 'processing' | 'success' | 'error';
interface SubmissionProgressProps {
  readonly phase: SubmissionPhase;
  readonly progress: number;
  readonly fileInfo?: {
    name: string;
    size: number;
  } | null | undefined;
  readonly error?: string | undefined;
}

/**
 * Formats file size in a human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Displays context-aware progress visualization for artwork submission
 * Follows a subtle, professional design that provides feedback without disruption
 */
const SubmissionProgress: React.FC<SubmissionProgressProps> = t0 => {
  const $ = _c(27);
  const {
    phase,
    progress,
    fileInfo,
    error
  } = t0;
  if (phase === "idle") {
    return null;
  }
  let t1;
  if ($[0] !== error || $[1] !== fileInfo || $[2] !== phase) {
    t1 = () => {
      switch (phase) {
        case "uploading":
          {
            if (fileInfo) {
              return `Uploading ${fileInfo.name} (${formatFileSize(fileInfo.size)})`;
            }
            return "Uploading artwork...";
          }
        case "signing":
          {
            return "Signing submission...";
          }
        case "processing":
          {
            return "Processing submission...";
          }
        case "success":
          {
            return "Submission complete!";
          }
        case "error":
          {
            return error || "Submission failed";
          }
        default:
          {
            return "";
          }
      }
    };
    $[0] = error;
    $[1] = fileInfo;
    $[2] = phase;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getMessage = t1;
  let t2;
  if ($[4] !== phase) {
    t2 = () => {
      switch (phase) {
        case "uploading":
          {
            return "tw-bg-blue-500";
          }
        case "signing":
          {
            return "tw-bg-yellow-500";
          }
        case "processing":
          {
            return "tw-bg-purple-500";
          }
        case "success":
          {
            return "tw-bg-green-500";
          }
        case "error":
          {
            return "tw-bg-red-500";
          }
        default:
          {
            return "tw-bg-gray-500";
          }
      }
    };
    $[4] = phase;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getProgressBarColor = t2;
  let t3;
  if ($[6] !== phase || $[7] !== progress) {
    t3 = () => {
      if (phase === "signing") {
        return "75%";
      }
      if (phase === "processing") {
        return "90%";
      }
      if (phase === "success") {
        return "100%";
      }
      if (phase === "error") {
        return "100%";
      }
      return `${Math.min(Math.max(progress, 0), 100)}%`;
    };
    $[6] = phase;
    $[7] = progress;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getProgressWidth = t3;
  let t4;
  if ($[9] !== getMessage) {
    t4 = getMessage();
    $[9] = getMessage;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <span>{t4}</span>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== phase || $[14] !== progress) {
    t6 = phase === "uploading" && <span className="tw-text-xs tw-text-gray-400">{progress}%</span>;
    $[13] = phase;
    $[14] = progress;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="tw-text-sm tw-text-gray-300 tw-mb-1 tw-flex tw-justify-between tw-items-center">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = `tw-h-full ${getProgressBarColor()} tw-transition-all tw-duration-300 tw-ease-in-out`;
  const t9 = getProgressWidth();
  let t10;
  if ($[19] !== t9) {
    t10 = {
      width: t9
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <div className="tw-w-full tw-h-1 tw-bg-iron-800 tw-rounded-full tw-overflow-hidden"><div className={t8} style={t10} /></div>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t7) {
    t12 = <div className="tw-w-full tw-my-4 tw-transition-all tw-duration-300 tw-ease-in-out" data-testid="submission-progress">{t7}{t11}</div>;
    $[24] = t11;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
export default React.memo(SubmissionProgress);